import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CombinedDownload } from '@goldcast/api/content';
import moment from 'moment';
import { SESSION_STORAGE_DOWNLOAD_PROGRESS_KEY } from './constants';
import { downloadURI } from '@/libs/core';
import Icon from '@/components/atoms/Icon';

interface DownloadsPopupListItemProps {
  download: CombinedDownload;
}

const DownloadsPopupListItem = ({ download }: DownloadsPopupListItemProps) => {
  const [progress, setProgress] = useState<number>(() => {
    if (download.status === 'DONE') {
      return 100;
    }
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_DOWNLOAD_PROGRESS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const savedProgress = parsed[download.id];
        return savedProgress ? Number.parseInt(savedProgress, 10) : 0;
      }
    } catch (error) {
      console.error('Failed to parse download progress from sessionStorage:', error);
    }
    return 0;
  });

  const timeAgo = useMemo(() => {
    if (!download.updated_at) {
      return;
    }
    return moment(new Date(download.updated_at)).fromNow();
  }, [download.updated_at]);

  const formattedUpdatedAt = useMemo(() => {
    if (!download.updated_at) {
      return;
    }
    return moment(new Date(download.updated_at)).format('MMM D, YYYY [at] h:mm A');
  }, [download.updated_at]);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_DOWNLOAD_PROGRESS_KEY) || '{}';
      const progressData = JSON.parse(stored);

      if (download.status === 'FAILED' || download.status === 'DONE' || progress === 100) {
        delete progressData[download.id];
      } else {
        progressData[download.id] = progress;
      }

      sessionStorage.setItem(SESSION_STORAGE_DOWNLOAD_PROGRESS_KEY, JSON.stringify(progressData));
    } catch (error) {
      console.error('Failed to update download progress in sessionStorage:', error);
    }
  }, [progress, download.id, download.status]);

  useEffect(() => {
    if (download.download_path) {
      return;
    }

    if (!download.download_estimated_time) {
      setProgress(0);
      return;
    }

    const updateProgress = () => {
      if (download.download_path) {
        setProgress(100);
        return;
      }

      const now = Date.now();
      const total = download.download_estimated_time * 1000;
      const elapsed = now - new Date(download.created_at).getTime();
      const percentage = Math.floor(Math.min((elapsed / total) * 100, 100));

      if (percentage >= 100 && !download.download_path) {
        setProgress(99);
        return;
      }

      setProgress(percentage);
    };

    const interval = setInterval(updateProgress, 1000);

    return () => clearInterval(interval);
  }, [download.created_at, download.download_path, download.download_estimated_time]);

  const onDownloadClick = useCallback(() => {
    if (!download.download_path) {
      return;
    }
    downloadURI(download.download_path);
  }, [download.download_path]);

  const statusConfig = useMemo(() => {
    if (download.status === 'FAILED') {
      return { icon: <Icon name="IconX" className="size-3.5" />, text: 'Failed', suffix: '' };
    }

    if (progress < 100) {
      return {
        icon: <Icon name="IconLoader2" className="size-3.5 animate-spin" />,
        text: 'Processing',
        suffix: `${progress}%`
      };
    }

    return { icon: <Icon name="IconCheck" className="size-3.5" />, text: 'Ready to Download', suffix: timeAgo };
  }, [download.status, progress, timeAgo]);

  return (
    <div className="mx-3 mb-3 flex items-center gap-2 rounded-xl border border-gray-200 bg-white p-2.5 transition-colors hover:bg-gray-50">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-slate-100">
        {download.type === 'CLIP' ? (
          <Icon name="IconVideo" className="size-4 text-slate-600" />
        ) : (
          <Icon name="IconFileTextAi" className="size-4 text-slate-600" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-center gap-1.5">
          <span className="truncate text-sm font-normal text-gray-900" title={download.file_name ?? download.title}>
            {download.file_name || download.title || 'Untitled'}
          </span>
          {download.post_processed_config?.upscale && (
            <div title="Uses AI Upscaling" className="shrink-0">
              <Icon name="IconWindowMaximize" className="size-3.5 text-gray-400" />
            </div>
          )}
        </div>
        <div className="flex space-x-1 text-2xs text-gray-500">
          <div className="flex items-center space-x-0.5">
            {statusConfig.icon}
            <span className="font-medium">{statusConfig.text}</span>
          </div>
          {statusConfig.suffix && <div>·</div>}
          <div className="truncate" title={formattedUpdatedAt}>
            {statusConfig.suffix}
          </div>
        </div>
        {progress !== 100 && download.status !== 'FAILED' && (
          <div className="mt-2 w-full">
            <div className="h-1 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full rounded-full bg-theme-active transition-all duration-500 ease-in-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>
      <button
        onClick={onDownloadClick}
        disabled={progress !== 100}
        className="flex size-9 shrink-0 items-center justify-center rounded-lg text-gray-900 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
        title="Download"
      >
        <Icon name="IconDownload" className="size-5" />
      </button>
    </div>
  );
};

export default DownloadsPopupListItem;
